import React, { useState, useEffect } from 'react';
import { Trash2, Copy, GripVertical } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import useTrainingPlanStore from '../../stores/trainingPlanStore';
import RpeHeatSlider from './RpeHeatSlider';

const numInput =
  '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none';

export default function SetRow({ setId, exerciseId }) {
  const set = useTrainingPlanStore(state => state.sets[setId]);
  const updateSet = useTrainingPlanStore(state => state.updateSet);
  const duplicateSet = useTrainingPlanStore(state => state.duplicateSet);
  const removeSet = useTrainingPlanStore(state => state.removeSet);
  
  const [language, setLanguage] = useState(() => localStorage.getItem('language') || 'english');

  const [localMinReps, setLocalMinReps] = useState('');
  const [localMaxReps, setLocalMaxReps] = useState('');
  const [localWeight, setLocalWeight] = useState('');

  useEffect(() => {
    const handleStorageChange = () => setLanguage(localStorage.getItem('language') || 'english');
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('languageChange', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('languageChange', handleStorageChange);
    };
  }, []);

  useEffect(() => {
    if (set) {
      setLocalMinReps(set.min_reps ?? '');
      setLocalMaxReps(set.max_reps ?? '');
      setLocalWeight(set.target_weight || '');
    }
  }, [set?.min_reps, set?.max_reps, set?.target_weight]);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: setId,
    data: { type: 'set', id: setId, exerciseId }
  });

  if (!set) return null;

  const he = language === 'hebrew';
  const dragStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 1,
    opacity: isDragging ? 0.8 : 1
  };

  const handleBlur = (field, localValue) => {
    const parsed = field === 'target_weight' ? localValue : (parseInt(localValue, 10) || 0);
    if (parsed !== set[field]) {
      updateSet(setId, { [field]: parsed });
    }
  };

  const handleKeyDown = (e, field, localValue) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  const commitRepRange = (side) => {
    let min = parseInt(String(localMinReps), 10);
    let max = parseInt(String(localMaxReps), 10);
    if (!Number.isFinite(min) || min < 0) min = 0;
    if (!Number.isFinite(max) || max < 0) max = 0;
    if (min > max) {
      if (side === 'min') max = min;
      else min = max;
    }
    setLocalMinReps(String(min));
    setLocalMaxReps(String(max));
    if (min !== set.min_reps || max !== set.max_reps) {
      updateSet(setId, { min_reps: min, max_reps: max });
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={dragStyle}
      className={`p-1.5 sm:p-2 bg-surface dark:bg-surface-dark border rounded-md group transition-[border-color,opacity] duration-120 space-y-2 ${
        isDragging ? 'border-forest' : 'border-line dark:border-line-dark hover:border-forest/50'
      }`}
    >
    <div className="grid grid-cols-[44px_minmax(0,1fr)_auto] sm:grid-cols-[44px_minmax(0,1fr)_minmax(8.5rem,1.1fr)_72px_96px] gap-2 items-center">
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="order-1 sm:order-none min-h-11 min-w-11 inline-flex flex-col items-center justify-center gap-0.5 cursor-grab active:cursor-grabbing text-ink-muted hover:text-ink dark:hover:text-ink-dark rounded-md outline-none focus-visible:ring-2 focus-visible:ring-forest touch-none"
        aria-label={he ? 'גרור לשינוי סדר הסט' : 'Drag to reorder set'}
      >
        <GripVertical size={16} />
        <span className="text-[11px] font-semibold tabular-nums leading-none">{set.set_index}</span>
      </button>

      <select 
        value={set.set_type}
        onChange={(e) => updateSet(setId, { set_type: e.target.value })}
        className="order-2 sm:order-none w-full min-h-11 bg-canvas dark:bg-canvas-dark border border-line dark:border-line-dark rounded-md px-1 sm:px-2 text-[13px] outline-none focus:border-forest focus:ring-2 focus:ring-forest font-medium text-ink dark:text-ink-dark"
      >
        <option value="working">{he ? 'עבודה' : 'Working'}</option>
        <option value="warmup">{he ? 'חימום' : 'Warmup'}</option>
        <option value="drop">{he ? 'דרופ-סט' : 'Drop'}</option>
        <option value="failure">{he ? 'לכשל' : 'Failure'}</option>
      </select>

      {/* Phones: reps and weight share the second line. From sm up this wrapper dissolves into the grid. */}
      <div className="order-4 sm:order-none col-span-3 grid grid-cols-[minmax(0,1fr)_6rem] gap-2 sm:contents">
      <div
        dir="ltr"
        className="flex items-stretch min-h-11 rounded-md border border-line dark:border-line-dark bg-canvas dark:bg-canvas-dark overflow-hidden focus-within:border-forest focus-within:ring-2 focus-within:ring-forest"
      >
        <label className="flex-1 min-w-0 flex flex-col items-center justify-center px-0.5">
          <span className="sr-only">{he ? 'מינימום חזרות' : 'Minimum reps'}</span>
          <input
            type="number"
            inputMode="numeric"
            min={0}
            value={localMinReps}
            onChange={(e) => setLocalMinReps(e.target.value)}
            onBlur={() => commitRepRange('min')}
            onKeyDown={(e) => e.key === 'Enter' && e.target.blur()}
            className={`w-full min-h-[28px] text-center bg-transparent border-0 outline-none text-[13px] font-semibold tabular-nums text-ink dark:text-ink-dark ${numInput}`}
          />
          <span className="text-[10px] leading-none text-ink-muted pb-1">{he ? 'מינ׳' : 'min'}</span>
        </label>
        <div className="flex items-center shrink-0 px-0.5" aria-hidden="true">
          <span className="h-px w-2 bg-line dark:bg-line-dark" />
          <span className="px-0.5 text-[13px] font-medium text-ink-muted">–</span>
          <span className="h-px w-2 bg-line dark:bg-line-dark" />
        </div>
        <label className="flex-1 min-w-0 flex flex-col items-center justify-center px-0.5">
          <span className="sr-only">{he ? 'מקסימום חזרות' : 'Maximum reps'}</span>
          <input
            type="number"
            inputMode="numeric"
            min={0}
            value={localMaxReps}
            onChange={(e) => setLocalMaxReps(e.target.value)}
            onBlur={() => commitRepRange('max')}
            onKeyDown={(e) => e.key === 'Enter' && e.target.blur()}
            className={`w-full min-h-[28px] text-center bg-transparent border-0 outline-none text-[13px] font-semibold tabular-nums text-ink dark:text-ink-dark ${numInput}`}
          />
          <span className="text-[10px] leading-none text-ink-muted pb-1">{he ? 'מקס׳' : 'max'}</span>
        </label>
      </div>

      <input 
        type="text"
        aria-label={he ? 'משקל' : 'Weight'}
        value={localWeight}
        onChange={(e) => setLocalWeight(e.target.value)}
        onBlur={() => handleBlur('target_weight', localWeight)}
        onKeyDown={(e) => handleKeyDown(e, 'target_weight', localWeight)}
        placeholder="kg/lbs"
        className="w-full min-h-11 text-center bg-canvas dark:bg-canvas-dark border border-line dark:border-line-dark rounded-md px-1 text-[13px] outline-none focus:border-forest focus:ring-2 focus:ring-forest font-semibold placeholder:text-[10px] placeholder:font-normal placeholder:text-ink-muted"
      />
      </div>

      <div className="order-3 sm:order-none flex justify-end gap-0.5">
        <button
          type="button"
          onClick={() => duplicateSet(exerciseId, setId)}
          className="min-h-11 min-w-11 flex items-center justify-center text-ink-muted hover:text-forest hover:bg-forest-faint dark:hover:bg-forest-faint-dark rounded-md transition-colors duration-120 outline-none focus-visible:ring-2 focus-visible:ring-forest"
          aria-label={he ? 'שכפל סט' : 'Duplicate set'}
        >
          <Copy size={16} />
        </button>
        <button
          type="button"
          onClick={() => removeSet(exerciseId, setId)}
          className="min-h-11 min-w-11 flex items-center justify-center text-ink-muted hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-md transition-colors duration-120 outline-none focus-visible:ring-2 focus-visible:ring-forest"
          aria-label={he ? 'מחק סט' : 'Delete set'}
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>

      <RpeHeatSlider
        value={set.target_rpe}
        he={he}
        onChange={(n) => {
          if (n !== set.target_rpe) updateSet(setId, { target_rpe: n });
        }}
      />
    </div>
  );
}
